/**
 * useScheduleDraftTrackMutations — ScheduleDraft 轨级写操作 hook 集合。
 *
 * 本模块把轨创建、删除、保存为历史快照、恢复和 Agent 轨采纳封装为
 * React Query mutation。调用方只关心轨语义，不直接接触 API 路径或缓存失效。
 *
 * 设计文档 → docs/10_core_contracts/10_data_model.md
 * 骨架关联 → services/scheduleDraftDataService.js (轨 API), hooks/useScheduleDraftMutationHelper.js (缓存失效), hooks/useScheduleDraftTrackConfig.js (轨展开)
 */

import { useMutation, useQueryClient } from "react-query";
import { scheduleDraftApi } from "../../../../api/v3/scheduleDraftApi";
import { scheduleDraftQueryKeys } from "./queryKeys";
import { useScheduleDraftMutationHelper } from "./useScheduleDraftMutationHelper";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";

function getTrackInvalidationKeys(draftId) {
    return [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.tracks(draftId), scheduleDraftQueryKeys.lists()];
}

export function useSaveTrackAsHistory(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: getTrackInvalidationKeys(draftId),
        ...options,
    });

    return useMutation(
        ({ trackId, values = {} }) =>
            scheduleDraftDataService.saveTrackAsHistory(draftId, trackId, {
                name: values.name?.trim() || values.summary?.trim() || undefined,
                summary: values.summary?.trim() || undefined,
            }),
        buildMutationOptions(),
    );
}

export function useReorderTracks() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ draftId, trackOrder }) =>
            scheduleDraftApi.reorderScheduleDraftTracks(draftId, trackOrder),
        onSuccess: (_, { draftId }) => {
            queryClient.invalidateQueries({ queryKey: scheduleDraftQueryKeys.tracks(draftId) });
        },
    });
}

export function useCloneTrack() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ draftId, sourceTrackId, name }) =>
            scheduleDraftApi.cloneScheduleDraftTrack(draftId, sourceTrackId, name),
        onSuccess: (_, { draftId }) => {
            queryClient.invalidateQueries({ queryKey: scheduleDraftQueryKeys.tracks(draftId) });
            queryClient.invalidateQueries({ queryKey: scheduleDraftQueryKeys.detail(draftId) });
        },
    });
}

export function useRestoreTrack(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: getTrackInvalidationKeys(draftId),
        ...options,
    });

    return useMutation(
        (trackId) => scheduleDraftDataService.restoreTrack(draftId, trackId),
        buildMutationOptions(),
    );
}

export function useAdoptAgentTrack(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: getTrackInvalidationKeys(draftId),
        ...options,
    });

    return useMutation(
        ({ trackId, values = {} }) => scheduleDraftDataService.adoptAgentTrack(draftId, trackId, values),
        buildMutationOptions(),
    );
}

export function useFinalizeTrack(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: [
            scheduleDraftQueryKeys.detail(draftId),
            scheduleDraftQueryKeys.tracks(draftId),
            scheduleDraftQueryKeys.flights(draftId),
            scheduleDraftQueryKeys.matchesRoot(draftId),
            scheduleDraftQueryKeys.executionFlights(),
            scheduleDraftQueryKeys.lists(),
        ],
        ...options,
    });

    return useMutation(
        (trackId) => scheduleDraftDataService.finalizeTrack(draftId, trackId),
        buildMutationOptions(),
    );
}

export function useCreateTrack(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: getTrackInvalidationKeys(draftId),
        ...options,
    });

    return useMutation(
        (values = {}) => scheduleDraftDataService.createTrack(draftId, values),
        buildMutationOptions(),
    );
}

export function useDeleteTrack(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: getTrackInvalidationKeys(draftId),
        ...options,
    });

    return useMutation(
        (trackId) => scheduleDraftDataService.deleteTrack(draftId, trackId),
        buildMutationOptions(),
    );
}

const scheduleDraftTrackMutations = {
    useSaveTrackAsHistory,
    useReorderTracks,
    useCloneTrack,
    useRestoreTrack,
    useAdoptAgentTrack,
    useFinalizeTrack,
    useCreateTrack,
    useDeleteTrack,
};

export default scheduleDraftTrackMutations;
